import React, { useState } from 'react';
import { Bot, Send, Loader2, TrendingUp, X, Minimize2, Maximize2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardAIAssistant = ({ stats, recentQuotes }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`admin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const buildContext = () => {
    const context = {
      stats: stats || {},
      recent_quotes_count: recentQuotes?.length || 0,
      recent_quotes_sample: recentQuotes?.slice(0, 3).map(q => ({
        name: q.name,
        service: q.selectedService,
        price: q.estimatedPrice,
        status: q.status
      })) || []
    };
    return JSON.stringify(context);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, id: `user-${Date.now()}` };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/ai/chat/admin`,
        {
          message: input,
          session_id: sessionId,
          context: buildContext()
        },
        { withCredentials: true } // Important for admin cookie authentication
      );

      const aiMessage = { role: 'assistant', content: response.data.response, id: `ai-${Date.now()}` };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Admin AI error:', error);
      const errorMessage = {
        role: 'assistant',
        content: error.response?.status === 401 
          ? "Authentication required. Please ensure you're logged in as admin."
          : "I'm having trouble connecting. Please try again in a moment.",
        id: `error-${Date.now()}`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `👋 Hi! I'm your admin assistant powered by GPT 6 Sol. I can help you analyze your dashboard data, suggest business strategies, and answer questions about your quotes and performance. What would you like to know?`,
        id: 'welcome-admin'
      }]);
    }
  };

  const suggestedQuestions = [
    "What's my conversion rate?",
    "Which service is most popular?",
    "How can I improve revenue?",
    "Analyze my pending quotes"
  ];

  if (!isOpen) {
    return (
      <button
        onClick={startConversation}
        className="fixed bottom-6 left-6 bg-gradient-to-r from-sky-600 to-purple-600 hover:from-sky-700 hover:to-purple-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-50 group"
        aria-label="Open Admin AI Assistant"
      >
        <Bot className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 bg-green-500 w-3 h-3 rounded-full animate-pulse"></span>
        <div className="absolute bottom-full left-0 mb-2 bg-slate-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Admin AI Assistant
        </div>
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 left-6 bg-gradient-to-r from-sky-600 to-purple-600 text-white rounded-xl shadow-2xl p-4 z-50 flex items-center gap-3 cursor-pointer hover:from-sky-700 hover:to-purple-700 transition-all">
        <Bot className="w-5 h-5" />
        <span className="font-semibold">Admin AI</span>
        <button
          onClick={() => setIsMinimized(false)}
          className="ml-2 hover:bg-white/20 p-1 rounded"
          aria-label="Maximize"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-1 rounded"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border-2 border-sky-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 to-purple-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Admin AI</h3>
            <p className="text-xs text-sky-100">GPT 6 Sol • Business Intelligence</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(true)}
            className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            aria-label="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Suggested Questions (only show initially) */}
      {messages.length <= 1 && (
        <div className="p-4 bg-sky-50 border-b border-sky-100">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-sky-600" />
            <h4 className="text-xs font-semibold text-slate-700">Quick insights:</h4>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {suggestedQuestions.map((q) => (
              <button
                key={q}
                onClick={() => setInput(q)}
                className="text-left text-xs p-2 bg-white hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors text-slate-700"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                msg.role === 'user'
                  ? 'bg-gradient-to-r from-sky-600 to-purple-600 text-white rounded-br-sm'
                  : 'bg-white text-slate-800 shadow-md rounded-bl-sm'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white text-slate-800 shadow-md px-4 py-3 rounded-2xl rounded-bl-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
                <span className="text-sm text-slate-600">Analyzing...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about your business..."
            disabled={loading}
            className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-600 disabled:bg-slate-100 text-sm"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-gradient-to-r from-sky-600 to-purple-600 hover:from-sky-700 hover:to-purple-700 disabled:bg-slate-300 text-white px-4 py-3 rounded-xl transition-colors disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2 text-center">
          Powered by GPT 6 Sol • Admin Only
        </p>
      </div>
    </div>
  );
};

export default AdminDashboardAIAssistant;
